import React, { useState, useEffect, useRef } from 'react';
import { Mail, Linkedin, MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const ContactSection = () => {
  const [contactAnimated, setContactAnimated] = useState(false);
  const contactRef = useRef(null);

  useEffect(() => {
    const contactObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !contactAnimated) {
          setContactAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (contactRef.current) {
      contactObserver.observe(contactRef.current);
    }

    return () => {
      if (contactRef.current) {
        contactObserver.unobserve(contactRef.current);
      }
    };
  }, [contactAnimated]);

  return (
    <section 
      id="contact" 
      className="py-20 px-6 bg-stone-50"
      ref={contactRef}
    >
      <div className={`container mx-auto max-w-6xl ${contactAnimated ? 'contact-animate-active' : 'contact-animate-ready'}`}>
        <div className="text-center max-w-5xl mx-auto mb-16">
          <span className="text-blue-900 font-semibold tracking-wide uppercase text-sm contact-label">
            Partnership Opportunities
          </span>
          <h2 className="md:text-5xl !font-bold !text-4xl mt-3 mb-6 text-stone-900">
            <span className="contact-headline">
              Direct from <span className="text-blue-900">Italy</span>
            </span>
          </h2>
          <p className="text-lg text-stone-600 contact-subtext">
            Source exclusive and niche products at the best price, manage your portfolio with speed and efficiency.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 mb-6">
          {/* Left Side - ALL-FOOD in Numbers */}
          <div>
            <Card className="border-stone-200 shadow-lg h-full">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-stone-900 mb-6">
                  ALL-FOOD in numbers
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-blue-900" data-testid="stat-years">10+</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-stone-900 mb-2">
                        Years of experience
                      </h4>
                      <p className="text-stone-600">
                        Over a decade of expertise in Italian food sourcing and export
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-blue-900" data-testid="stat-clients">50+</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-stone-900 mb-2">
                        Happy clients
                      </h4>
                      <p className="text-stone-600">
                        Importers, distributors, and retailers across five continents
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-blue-900" data-testid="stat-products">200+</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-stone-900 mb-2">
                        Products sourced
                      </h4>
                      <p className="text-stone-600">
                        Authentic Italian products sourced directly from local producers
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-blue-900" data-testid="stat-quality">100%</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-stone-900 mb-2">
                        Quality guaranteed
                      </h4>
                      <p className="text-stone-600">
                        Every product verified on-site in Italy before export
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Contact Information */}
          <div>
            <Card className="border-stone-200 shadow-lg h-full">
              <CardContent className="p-8 space-y-4">
                <h3 className="text-2xl font-bold text-stone-900 mb-6">
                  Contact information
                </h3>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-blue-900" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">Address</p>
                    <p className="text-stone-600">
                      Piazza Arnolfo di Cambio, 25<br />
                      53034 Colle di Val d'Elsa (SI), Italy
                    </p>
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="border-blue-900 text-blue-900 hover:bg-blue-50 mt-3"
                      data-testid="contact-get-directions-btn"
                    >
                      <a
                        href="https://www.google.com/maps/dir/?api=1&destination=43.4224379,11.1268622"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Get directions to ALL-FOOD S.r.l. in Colle di Val d'Elsa"
                        className="inline-flex items-center gap-2"
                      >
                        <MapPin size={16} />
                        Get directions
                      </a>
                    </Button>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-blue-900" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">Phone</p>
                    <a href="tel:+393283915713" className="text-blue-900 hover:text-blue-950 transition-colors">
                      +39 328 391 5713
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-blue-900" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">Email</p>
                    <a href="mailto:global@all-food.it" className="text-blue-900 hover:text-blue-950 transition-colors">
                      global@all-food.it
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg 
                      viewBox="0 0 32 32" 
                      className="w-6 h-6 fill-[#25D366]"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958C9.726 30.972 12.734 32 16.004 32 24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.336 22.594c-.39 1.1-1.932 2.014-3.168 2.28-.846.18-1.952.324-5.674-1.22-4.762-1.974-7.828-6.82-8.066-7.136-.228-.316-1.916-2.55-1.916-4.864 0-2.314 1.212-3.452 1.644-3.924.39-.428 1.022-.624 1.624-.624.196 0 .372.01.53.018.472.02.708.048 1.02.788.39.926 1.34 3.268 1.458 3.506.118.238.236.558.078.876-.148.326-.276.472-.514.748-.238.276-.464.488-.702.784-.218.258-.464.534-.2.984.266.452 1.182 1.946 2.536 3.152 1.742 1.55 3.206 2.032 3.66 2.26.352.178.77.148 1.046-.138.346-.364.774-.966 1.21-1.56.31-.422.702-.474 1.094-.316.398.148 2.52 1.188 2.952 1.404.432.218.72.326.826.502.108.178.108 1.028-.282 2.128z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">WhatsApp</p>
                    <a 
                      href="https://wa.me/393283915713?text=Hello,%20I'm%20interested%20in%20learning%20more%20about%20ALL-FOOD's%20Italian%20export%20services." 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-700 hover:text-green-800 transition-colors"
                    >
                      Contact us on WhatsApp
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Linkedin className="text-blue-900" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900 mb-1">LinkedIn</p>
                    <a 
                      href="https://www.linkedin.com/company/all-food-s-r-l/" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-900 hover:text-blue-950 transition-colors"
                    >
                      Follow us for market updates
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Map - Full Width Below */}
        <Card className="border-stone-200 shadow-lg overflow-hidden">
          <CardContent className="p-0">
            <div className="h-80">
              <iframe
                title="ALL-FOOD S.r.l. location in Colle di Val d'Elsa"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3346.839221684418!2d11.124287276445893!3d43.42244176732479!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x200753977aec9053%3A0xaedcc838f6d0f7d0!2sALL-FOOD%20S.r.l.!5e0!3m2!1sen!2s!4v1788532452136!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactSection;
